// src/components/conciergerie/ConciergerieStats.jsx
//
// Conciergerie figures at the top of the owner's Alertes page
// (documentations/conciergerie-plan.md, step 6), from
// GET /v2/incidents/stats (backend/routes/incidents.ts).
//
// Form choices: the headline numbers are stat tiles, not charts; problems
// per category is a single-series bar list (one hue, no legend, value
// written beside each bar in text ink, tooltip on hover); the
// establishment comparison is a table.
import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Stack, Typography, ToggleButton, ToggleButtonGroup, Tooltip,
  Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

export function formatDuration(minutes) {
  if (minutes == null) return '—';
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  if (m < 48 * 60) return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
  return `${Math.round(m / 1440)} j`;
}

const pct = (rate) => (rate == null ? '—' : `${Math.round(rate * 100)} %`);

function Tile({ label, value, hint }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 140px', minWidth: 140 }}>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography sx={{ fontSize: 28, fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
    </Paper>
  );
}

export default function ConciergerieStats({ token }) {
  const { t } = useTranslation();
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    if (!token) return;
    setStats(null);
    fetch(`${API_BASE_URL}/v2/incidents/stats?days=${days}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then(setStats)
      .catch(() => setStats(null));
  }, [token, days]);

  const max = Math.max(1, ...(stats?.byCategory || []).map((c) => c.count));

  return (
    <Box sx={{ mb: 4 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6">{t('alerts.stats.title')}</Typography>
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
          {[7, 30, 90].map((d) => <ToggleButton key={d} value={d}>{t('alerts.stats.days', { days: d })}</ToggleButton>)}
        </ToggleButtonGroup>
      </Stack>

      {stats && (
        <>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1.5} sx={{ mb: 2 }}>
            <Tile label={t('alerts.stats.reports')} value={stats.reports}
              hint={t('alerts.stats.allGood', { count: stats.allGood })} />
            <Tile label={t('alerts.stats.problems')} value={stats.problems} />
            <Tile label={t('alerts.stats.resolved')} value={pct(stats.resolutionRate)} />
            <Tile label={t('alerts.stats.avgTime')} value={formatDuration(stats.avgResolutionMinutes)} />
            <Tile label={t('alerts.stats.within24h')} value={pct(stats.within24hRate)} hint={t('alerts.stats.within24hHint')} />
          </Stack>

          {stats.byCategory.length > 0 && (
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>{t('alerts.stats.byCategory')}</Typography>
              <Stack spacing={1} role="list">
                {stats.byCategory.map((c) => (
                  <Tooltip key={c.category} placement="top-start"
                    title={`${t(`alerts.category.${c.category}`)} : ${c.count} (${Math.round((c.count / stats.problems) * 100)} %)`}>
                    <Box role="listitem" sx={{ display: 'grid', gridTemplateColumns: '150px 1fr 32px', alignItems: 'center', gap: 1.5, py: 0.25 }}>
                      <Typography variant="body2" color="text.secondary" noWrap>{t(`alerts.category.${c.category}`)}</Typography>
                      <Box sx={{ height: 10, width: `${(c.count / max) * 100}%`, minWidth: 4, bgcolor: 'primary.main', borderRadius: '0 4px 4px 0' }} />
                      <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{c.count}</Typography>
                    </Box>
                  </Tooltip>
                ))}
              </Stack>
            </Paper>
          )}

          {stats.byFacility.length > 1 && (
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('alerts.stats.facility')}</TableCell>
                    <TableCell align="right">{t('alerts.stats.problems')}</TableCell>
                    <TableCell align="right">{t('alerts.stats.open')}</TableCell>
                    <TableCell align="right">{t('alerts.stats.avgTime')}</TableCell>
                    <TableCell align="right">{t('alerts.stats.rating')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stats.byFacility.map((f) => (
                    <TableRow key={f.facilityId}>
                      <TableCell>{f.name}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{f.problems}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{f.open}</TableCell>
                      <TableCell align="right">{formatDuration(f.avgResolutionMinutes)}</TableCell>
                      <TableCell align="right">{f.avgRating == null ? '—' : `${f.avgRating.toFixed(1)} / 5`}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </>
      )}
    </Box>
  );
}
